import React, { useState, useEffect, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { isProductionManagerRole } from '../../../lib/operatorRoles';

const BatchTrackingPanel = ({ batches, onBatchSelect, onRefresh, userRole, currentUser }) => {
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCase, setFilterCase] = useState('all');
  const [uniqueCases, setUniqueCases] = useState([]);
  const [loadingCases, setLoadingCases] = useState(false);
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [selectedBatchForLabel, setSelectedBatchForLabel] = useState(null);
  const [savingLabel, setSavingLabel] = useState(false);

  // Filter batches based on user role
  const visibleBatches = useMemo(() => {
    if (!batches) return [];
    
    if (isProductionManagerRole(userRole) && currentUser?.id) {
      return batches.filter(batch => 
        batch.responsible === currentUser.id || 
        batch.responsibleId === currentUser.id
      );
    }
    
    return batches;
  }, [batches, userRole, currentUser]);

  // Fetch unique cases from visible batches
  useEffect(() => {
    const fetchCases = async () => {
      if (!visibleBatches || visibleBatches.length === 0) {
        setUniqueCases([]);
        return;
      }

      try {
        setLoadingCases(true);
        
        const caseIds = [...new Set(visibleBatches.map(batch => batch?.case_id).filter(Boolean))];
        
        if (caseIds.length === 0) {
          setUniqueCases([]);
          setLoadingCases(false);
          return;
        }

        const { data: casesData, error } = await supabase
          .from('cases')
          .select('id, case_number, patient:patients(id, name)')
          .in('id', caseIds)
          .order('case_number', { ascending: false });

        if (error) throw error;

        const casesMap = casesData?.map(caseItem => ({
          id: caseItem.id,
          caseNumber: caseItem.case_number,
          patientName: caseItem.patient?.name || 'Patient inconnu',
          batchCount: visibleBatches.filter(b => b.case_id === caseItem.id).length
        })) || [];

        setUniqueCases(casesMap);
      } catch (error) {
        console.error('Error fetching cases:', error);
        setUniqueCases([]);
      } finally {
        setLoadingCases(false);
      }
    };

    fetchCases();
  }, [visibleBatches]);

  // Get case info for a batch
  const getCaseInfo = (batch) => {
    if (!batch?.case_id) return null;
    return uniqueCases.find(c => c.id === batch.case_id);
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: { label: 'En attente', className: 'bg-yellow-100 text-yellow-800', icon: 'Clock' },
      in_progress: { label: 'En cours', className: 'bg-blue-100 text-blue-800', icon: 'Play' },
      quality_check: { label: 'Contrôle qualité', className: 'bg-purple-100 text-purple-800', icon: 'CheckCircle' },
      packaging: { label: 'Emballage', className: 'bg-orange-100 text-orange-800', icon: 'Package' },
      completed: { label: 'Terminé', className: 'bg-green-100 text-green-800', icon: 'Check' },
      on_hold: { label: 'En pause', className: 'bg-gray-100 text-gray-800', icon: 'Pause' },
      cancelled: { label: 'Annulé', className: 'bg-red-100 text-red-800', icon: 'X' }
    };
    return configs[status] || configs.pending;
  };

  const getPriorityBadge = (priority) => {
    const configs = {
      low: { label: 'Basse', className: 'bg-gray-100 text-gray-700' },
      medium: { label: 'Moyenne', className: 'bg-blue-100 text-blue-700' },
      high: { label: 'Haute', className: 'bg-orange-100 text-orange-700' },
      urgent: { label: 'Urgente', className: 'bg-red-100 text-red-700' }
    };
    return configs[priority] || configs.medium;
  };

  // Handle generate label click
  const handleGenerateLabel = (batch, e) => {
    e.stopPropagation();
    setSelectedBatchForLabel(batch);
    setShowLabelModal(true);
  };

  // Handle save label and update status
  const handleSaveLabel = async () => {
    if (!selectedBatchForLabel) return;

    try {
      setSavingLabel(true);

      const { error } = await supabase
        .from('production_batches')
        .update({
          status: 'completed',
          packaging_date: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedBatchForLabel.id);

      if (error) throw error;

      setShowLabelModal(false);
      setSelectedBatchForLabel(null);
      
      if (onRefresh) {
        await onRefresh();
      }

      alert('Étiquette générée avec succès ! Le lot a été marqué comme terminé.');
    } catch (error) {
      console.error('Error saving label:', error);
      alert('Erreur lors de la génération de l\'étiquette');
    } finally {
      setSavingLabel(false);
    }
  };

  // Filter visible batches by status and case
  const filteredBatches = visibleBatches?.filter(batch => {
    const statusMatch = filterStatus === 'all' || batch?.status === filterStatus;
    const caseMatch = filterCase === 'all' || batch?.case_id === filterCase;
    return statusMatch && caseMatch;
  }) || [];

  const packagingBatches = filteredBatches.filter(b => b?.status === 'packaging');
  const otherBatches = filteredBatches.filter(b => b?.status !== 'packaging');

  return (
    <div className="space-y-6">
      {/* User Role Notice */}
      {isProductionManagerRole(userRole) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-sm text-blue-800">
            <Icon name="Info" size={16} />
            <span className="font-medium">Affichage de vos lots uniquement</span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
        {/* Case Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Filtrer par cas
          </label>
          {loadingCases ? (
            <div className="text-sm text-gray-500">Chargement des cas...</div>
          ) : (
            <div className="flex items-center gap-4">
              <select
                value={filterCase}
                onChange={(e) => setFilterCase(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">
                  Tous les cas ({visibleBatches?.length || 0} lots)
                </option>
                {uniqueCases.map(caseItem => (
                  <option key={caseItem.id} value={caseItem.id}>
                    {caseItem.caseNumber} - {caseItem.patientName} ({caseItem.batchCount} lot{caseItem.batchCount > 1 ? 's' : ''})
                  </option>
                ))}
              </select>
              
              {filterCase !== 'all' && (
                <button
                  onClick={() => setFilterCase('all')}
                  className="text-sm text-gray-600 hover:text-gray-900"
                >
                  <Icon name="X" size={16} />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Active Filters Summary */}
        {(filterStatus !== 'all' || filterCase !== 'all') && (
          <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
            <span className="text-sm text-gray-600">Filtres actifs:</span>
            {filterStatus !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 text-primary text-xs">
                {getStatusConfig(filterStatus)?.label}
                <button
                  onClick={() => setFilterStatus('all')}
                  className="hover:bg-primary/20 rounded-full p-0.5"
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            )}
            {filterCase !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-purple-100 text-purple-700 text-xs">
                {uniqueCases.find(c => c.id === filterCase)?.caseNumber}
                <button
                  onClick={() => setFilterCase('all')}
                  className="hover:bg-purple-200 rounded-full p-0.5"
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            )}
            <button
              onClick={() => {
                setFilterStatus('all');
                setFilterCase('all');
              }}
              className="text-xs text-gray-600 hover:text-gray-900 ml-2"
            >
              Réinitialiser tout
            </button>
          </div>
        )}
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          <span className="font-semibold text-gray-900">{filteredBatches?.length}</span> lot(s) affiché(s)
        </p>
      </div>

      {/* Packaging Section */}
      {packagingBatches.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
              <Icon name="Package" size={20} className="text-orange-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Emballage</h3>
              <p className="text-sm text-gray-600">{packagingBatches.length} lot(s) prêt(s) pour l'emballage</p>
            </div>
          </div>

          <div className="space-y-3">
            {packagingBatches.map(batch => {
              const statusConfig = getStatusConfig(batch?.status);
              const priorityBadge = getPriorityBadge(batch?.priority);
              const caseInfo = getCaseInfo(batch);

              return (
                <div
                  key={batch?.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div 
                      className="flex-1 cursor-pointer"
                      onClick={() => onBatchSelect(batch)}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-gray-900">
                          {batch?.batchNumber || batch?.batch_number}
                        </h4>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${priorityBadge.className}`}>
                          {priorityBadge.label}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        {batch?.patientName || batch?.patient_name}
                      </p>
                      
                      {/* Case Info */}
                      <div className="flex items-center gap-2 mt-2">
                        {caseInfo && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-purple-50 text-purple-700 text-xs font-medium">
                            <Icon name="FolderOpen" size={12} />
                            {caseInfo.caseNumber}
                          </span>
                        )}
                        
                        {batch?.current_phase && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-xs font-medium">
                            <Icon name="Layers" size={12} />
                            Phase {batch.current_phase}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-2">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${statusConfig.className}`}>
                        <Icon name={statusConfig.icon} size={14} />
                        {statusConfig.label}
                      </span>
                      
                      {/* Generate Label Button */}
                      <button
                        onClick={(e) => handleGenerateLabel(batch, e)}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                      >
                        <Icon name="Tag" size={16} />
                        Générer étiquette
                      </button>
                    </div>
                  </div>

                  {/* Progress */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-gray-600">Progression</span>
                      <span className="font-semibold text-gray-900">{batch?.progress || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className="bg-orange-500 rounded-full h-1.5"
                        style={{ width: `${batch?.progress || 0}%` }}
                      />
                    </div>
                  </div>

                  {/* Additional Info */}
                  <div className="flex items-center gap-4 text-xs text-gray-600">
                    {batch?.deadline && (
                      <div className="flex items-center gap-1">
                        <Icon name="Calendar" size={12} />
                        <span>Échéance: {new Date(batch.deadline).toLocaleDateString('fr-FR')}</span>
                      </div>
                    )}
                    {batch?.packaging_date && (
                      <div className="flex items-center gap-1">
                        <Icon name="Package" size={12} />
                        <span>Emballé: {new Date(batch.packaging_date).toLocaleDateString('fr-FR')}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Other Batches */}
      {otherBatches.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Autres lots
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {otherBatches.map(batch => {
              const statusConfig = getStatusConfig(batch?.status);
              const priorityBadge = getPriorityBadge(batch?.priority);
              const caseInfo = getCaseInfo(batch);

              return (
                <div
                  key={batch?.id}
                  onClick={() => onBatchSelect(batch)}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-gray-900">
                          {batch?.batchNumber || batch?.batch_number}
                        </h4>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${priorityBadge.className}`}>
                          {priorityBadge.label}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        {batch?.patientName || batch?.patient_name}
                      </p>
                      
                      {/* Case Info */}
                      <div className="flex items-center gap-2 mt-2">
                        {caseInfo && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-purple-50 text-purple-700 text-xs font-medium">
                            <Icon name="FolderOpen" size={12} />
                            {caseInfo.caseNumber}
                          </span>
                        )}
                        
                        {batch?.current_phase && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-xs font-medium">
                            <Icon name="Layers" size={12} />
                            Phase {batch.current_phase}
                          </span>
                        )}
                      </div>
                    </div>

                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${statusConfig.className}`}>
                      <Icon name={statusConfig.icon} size={14} />
                      {statusConfig.label}
                    </span>
                  </div>

                  {/* Progress */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-gray-600">Progression</span>
                      <span className="font-semibold text-gray-900">{batch?.progress || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className="bg-primary rounded-full h-1.5"
                        style={{ width: `${batch?.progress || 0}%` }}
                      />
                    </div>
                  </div>

                  {/* Additional Info */}
                  <div className="flex items-center gap-4 text-xs text-gray-600">
                    {batch?.deadline && (
                      <div className="flex items-center gap-1">
                        <Icon name="Calendar" size={12} />
                        <span>{new Date(batch.deadline).toLocaleDateString('fr-FR')}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty State */}
      {filteredBatches.length === 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
          <Icon name="Package" size={64} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun lot trouvé</h3>
          <p className="text-sm text-gray-600 mb-4">
            {isProductionManagerRole(userRole)
              ? 'Aucun lot ne vous est assigné pour le moment'
              : 'Aucun lot ne correspond aux filtres sélectionnés'}
          </p>
          <button
            onClick={() => {
              setFilterStatus('all');
              setFilterCase('all');
            }}
            className="text-sm text-primary hover:underline"
          >
            Réinitialiser les filtres
          </button>
        </div>
      )}

      {/* Label Generation Modal */}
      {showLabelModal && selectedBatchForLabel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <Icon name="Tag" size={20} className="text-green-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Génération d'étiquette</h2>
                  <p className="text-sm text-gray-600">
                    Lot: {selectedBatchForLabel?.batchNumber || selectedBatchForLabel?.batch_number}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowLabelModal(false);
                  setSelectedBatchForLabel(null);
                }}
                className="text-gray-400 hover:text-gray-600"
                disabled={savingLabel}
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* Batch Info */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Numéro de lot</p>
                    <p className="font-semibold text-gray-900">
                      {selectedBatchForLabel?.batchNumber || selectedBatchForLabel?.batch_number}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Patient</p>
                    <p className="font-semibold text-gray-900">
                      {selectedBatchForLabel?.patientName || selectedBatchForLabel?.patient_name}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Statut actuel</p>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusConfig(selectedBatchForLabel?.status).className}`}>
                      <Icon name={getStatusConfig(selectedBatchForLabel?.status).icon} size={12} />
                      {getStatusConfig(selectedBatchForLabel?.status).label}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Progression</p>
                    <p className="font-semibold text-gray-900">{selectedBatchForLabel?.progress || 0}%</p>
                  </div>
                </div>
              </div>

              {/* Label Preview */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <div className="inline-block bg-white border-2 border-gray-900 rounded-lg p-6 shadow-lg">
                  <div className="space-y-4">
                    <div className="text-center">
                      <p className="text-xs text-gray-600 mb-1">LOT DE PRODUCTION</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {selectedBatchForLabel?.batchNumber || selectedBatchForLabel?.batch_number}
                      </p>
                    </div>
                    
                    <div className="border-t border-gray-300 pt-3">
                      <p className="text-sm text-gray-700">
                        <span className="font-semibold">Patient:</span> {selectedBatchForLabel?.patientName || selectedBatchForLabel?.patient_name}
                      </p>
                      {getCaseInfo(selectedBatchForLabel) && (
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold">Cas:</span> {getCaseInfo(selectedBatchForLabel)?.caseNumber}
                        </p>
                      )}
                      <p className="text-sm text-gray-700">
                        <span className="font-semibold">Date:</span> {new Date().toLocaleDateString('fr-FR')}
                      </p>
                    </div>

                    {/* QR Code Placeholder */}
                    <div className="flex justify-center pt-2">
                      <div className="w-32 h-32 bg-gray-200 rounded flex items-center justify-center">
                        <Icon name="QrCode" size={64} className="text-gray-400" />
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-4">Aperçu de l'étiquette</p>
              </div>

              {/* Warning Message */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex gap-3">
                  <Icon name="AlertTriangle" size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-yellow-900 mb-1">
                      Attention
                    </p>
                    <p className="text-sm text-yellow-700">
                      En enregistrant, le statut du lot sera automatiquement mis à jour vers <strong>"Terminé"</strong> et l'étiquette sera générée définitivement.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  setShowLabelModal(false);
                  setSelectedBatchForLabel(null);
                }}
                disabled={savingLabel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Annuler
              </button>
              <button
                onClick={handleSaveLabel}
                disabled={savingLabel}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {savingLabel ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Icon name="Save" size={16} />
                    Enregistrer et terminer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchTrackingPanel;